import {
  nhomColumns,
  QUERY_KEYS,
  regionSearchColumns,
  supplierSearchColumns,
  vatTuSearchColumns,
  warehouseSearchColumns
} from '@/constants';
import { DoiTuong, Group, KhoHang, KhuVuc, VatTu } from '@/types/schemas';
import { SearchDialogActions, SearchDialogState } from '../../hooks';
import { FormField, SearchField } from '@/components/custom/erp';
import { useLoaiVatTu } from '@/hooks/queries/useLoaiVatTu';
import { Label } from '@/components/ui/label';

interface GeneralTabProps {
  searchState: {
    state: SearchDialogState;
    actions: SearchDialogActions;
  };
}

const GeneralTab: React.FC<GeneralTabProps> = ({ searchState: { state, actions } }) => {
  const { loaiVatTus } = useLoaiVatTu();

  const loaiVatTuOptions = loaiVatTus.map(item => ({
    value: item.uuid,
    label: `${item.ten_lvt}`
  }));

  return (
    <div className='h-96 w-[800px] min-w-[800px] space-y-4 p-4'>
      <div className='flex flex-col space-y-3'>
        {/* Mã nhà cung cấp */}
        <div className='flex items-center'>
          <Label className='w-40 min-w-40 text-left'>Mã nhà cung cấp:</Label>
          <SearchField<DoiTuong>
            searchEndpoint={`/${QUERY_KEYS.NHA_CUNG_CAP}/`}
            searchColumns={supplierSearchColumns}
            dialogTitle='Danh mục nhà cung cấp'
            columnDisplay={`customer_code`}
            displayRelatedField={`customer_name`}
            value={state.khachHang?.customer_code || ''}
            relatedFieldValue={state.khachHang?.customer_name || ''}
            onRowSelection={actions.setKhachHang}
            className='w-full'
          />
        </div>

        {/* Nhóm nhà cung cấp - 3 field trong 1 hàng */}
        <div className='flex items-center'>
          <Label className='w-40 min-w-40 text-left'>Nhóm NCC:</Label>
          <div className='flex gap-3'>
            <div className='flex-1'>
              <SearchField<Group>
                searchEndpoint={`/${QUERY_KEYS.NHOM}/?loai_nhom=NCC1`}
                searchColumns={nhomColumns}
                dialogTitle='Danh mục nhóm khách hàng 1'
                columnDisplay={`ma_nhom`}
                value={state.nhomKhachHang1?.ma_nhom || ''}
                relatedFieldValue={state.nhomKhachHang1?.ten_phan_nhom || ''}
                onRowSelection={actions.setNhomKhachHang1}
                className='w-48'
              />
            </div>
            <div className='flex-1'>
              <SearchField<Group>
                searchEndpoint={`/${QUERY_KEYS.NHOM}/?loai_nhom=NCC2`}
                searchColumns={nhomColumns}
                dialogTitle='Danh mục nhóm khách hàng 2'
                columnDisplay={`ma_nhom`}
                value={state.nhomKhachHang2?.ma_nhom || ''}
                relatedFieldValue={state.nhomKhachHang2?.ten_phan_nhom || ''}
                onRowSelection={actions.setNhomKhachHang2}
                className='w-48'
              />
            </div>
            <div className='flex-1'>
              <SearchField<Group>
                searchEndpoint={`/${QUERY_KEYS.NHOM}/?loai_nhom=NCC3`}
                searchColumns={nhomColumns}
                dialogTitle='Danh mục nhóm khách hàng 3'
                columnDisplay={`ma_nhom`}
                value={state.nhomKhachHang3?.ma_nhom || ''}
                relatedFieldValue={state.nhomKhachHang3?.ten_phan_nhom || ''}
                onRowSelection={actions.setNhomKhachHang3}
                className='w-48'
              />
            </div>
          </div>
        </div>

        {/* Khu vực */}
        <div className='flex items-center'>
          <Label className='w-40 min-w-40 text-left'>Khu vực:</Label>
          <SearchField<KhuVuc>
            searchEndpoint={`/${QUERY_KEYS.KHU_VUC}/`}
            searchColumns={regionSearchColumns}
            dialogTitle='Danh mục khu vực'
            columnDisplay={`rg_code`}
            displayRelatedField={`rgname`}
            value={state.khuVuc?.rg_code || ''}
            relatedFieldValue={state.khuVuc?.rgname || ''}
            onRowSelection={actions.setKhuVuc}
            className='w-full'
          />
        </div>

        {/* Mã vật tư */}
        <div className='flex items-center'>
          <Label className='w-40 min-w-40 text-left'>Mã vật tư:</Label>
          <SearchField<VatTu>
            type='text'
            searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
            searchColumns={vatTuSearchColumns}
            dialogTitle='Danh mục vật tư'
            columnDisplay={`ma_vt`}
            displayRelatedField={`ten_vt`}
            value={state.vatTu?.ma_vt || ''}
            relatedFieldValue={state.vatTu?.ten_vt || ''}
            onRowSelection={actions.setVatTu}
            className='w-full'
          />
        </div>

        {/* Loại vật tư */}
        <div className='flex items-center'>
          <Label className='w-40 min-w-40 text-left'>Loại vật tư:</Label>
          <div className='w-40'>
            <FormField name='ma_lvt' type='select' options={loaiVatTuOptions} className='w-full' />
          </div>
          {/* Chỉ xem vật tư có theo dõi tồn kho */}
          <div className='flex items-center'>
            <FormField name='ton_kho_yn' type='checkbox' className='flex-1' />
            <Label className='w-80 min-w-40 text-left'>Chỉ xem vật tư có theo dõi tồn kho</Label>
          </div>
        </div>

        {/* Nhóm vật tư - 3 field trong 1 hàng */}
        <div className='flex items-center'>
          <Label className='w-40 min-w-40 text-left'>Nhóm vật tư:</Label>
          <div className='flex gap-3'>
            <div className='flex-1'>
              <SearchField<Group>
                searchEndpoint={`/${QUERY_KEYS.NHOM}/?loai_nhom=VT1`}
                searchColumns={nhomColumns}
                dialogTitle='Danh mục nhóm vật tư'
                columnDisplay={`ma_nhom`}
                value={state.nhomVatTu1?.ma_nhom || ''}
                onRowSelection={actions.setNhomVatTu1}
                className='w-48'
              />
            </div>
            <div className='flex-1'>
              <SearchField<Group>
                searchEndpoint={`/${QUERY_KEYS.NHOM}/?loai_nhom=VT2`}
                searchColumns={nhomColumns}
                dialogTitle='Danh mục nhóm vật tư 2'
                columnDisplay={`ma_nhom`}
                value={state.nhomVatTu2?.ma_nhom || ''}
                onRowSelection={actions.setNhomVatTu2}
                className='w-48'
              />
            </div>
            <div className='flex-1'>
              <SearchField<Group>
                searchEndpoint={`/${QUERY_KEYS.NHOM}/?loai_nhom=VT3`}
                searchColumns={nhomColumns}
                dialogTitle='Danh mục nhóm vật tư 3'
                columnDisplay={`ma_nhom`}
                value={state.nhomVatTu3?.ma_nhom || ''}
                onRowSelection={actions.setNhomVatTu3}
                className='w-48'
              />
            </div>
          </div>
        </div>

        {/* Mã kho */}
        <div className='flex items-center'>
          <Label className='w-40 min-w-40 text-left'>Mã kho:</Label>
          <SearchField<KhoHang>
            searchEndpoint={`/${QUERY_KEYS.KHO_HANG}/`}
            searchColumns={warehouseSearchColumns}
            dialogTitle='Danh mục kho hàng'
            columnDisplay={`ma_kho`}
            displayRelatedField={`ten_kho`}
            value={state.khoHang?.ma_kho || ''}
            relatedFieldValue={state.khoHang?.ten_kho || ''}
            onRowSelection={actions.setKhoHang}
          />
        </div>

        {/* Mẫu báo cáo */}
        <div className='flex items-center'>
          <Label className='w-40 min-w-40 text-left'>Mẫu báo cáo:</Label>
          <div className='w-64'>
            <FormField
              name='mau_bc'
              type='select'
              options={[
                { value: 10, label: 'Mẫu số lượng' },
                { value: 20, label: 'Mẫu số lượng và giá trị' },
                { value: 30, label: 'Mẫu số lượng và giá trị ngoại tệ' }
              ]}
              className='w-full'
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralTab;
